import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import Heading from "./Heading";
import Footer from "./Footer";
import { Button, Card, CardActions, CardContent, Container, Stack, ThemeProvider, Typography, } from "@mui/material";
import getTheme from "./Theme";
import { getLayoutInfo } from "./LayoutInfo";
import { useEffect, useState } from "react";
import { restoreSession, useProfileAuthStore } from "./store/ProfileStore";
import { PostCard } from "./components/PostCard";
import { getContentURL, getSiteBaseURL } from "./ContentAPI";
import { ContentCardStacks } from "./components/ContentCardStacks";
import { ChannelButton } from "./components/ChannelButton";
import { deletePost, loadChannelDataFromURL, } from "./util/DataUtils";
import { useAgeVerificationGating } from "./hooks/useAgeVerificationGating";
import { useCacheBusterStore } from "./util/UrlUtils";
import { useLogger } from "./logger";
import { OpenGraphMeta } from "./components/OpenGraphMeta";
import { generatePostOpenGraphData } from "./util/OpenGraphUtils";
import { useMetaTagPriority } from "./hooks/useMetaTagPriority";
export const PostView = () => {
    // Ensure Open Graph tags stay at the top of the head
    useMetaTagPriority();
    const [errorMessage, setErrorMessage] = useState();
    const [channelOwnerId, setChannelOwnerId] = useState();
    const [channelId, setChannelId] = useState();
    const [postId, setPostId] = useState();
    const [postDeletedOK, setPostDeletedOK] = useState(false);
    const [postData, setPostData] = useState();
    const [channelData, setChannelData] = useState();
    const [height, setHeight] = useState();
    const [width, setWidth] = useState();
    const auth = useProfileAuthStore((state) => state);
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const ageVerification = useAgeVerificationGating();
    const logger = useLogger((state) => state);
    const layout = getLayoutInfo();
    useEffect(() => {
        if (!auth.isSignedIn() || !auth.userId) {
            if (!restoreSession(auth, logger)) {
                logger.log("User NOT logged in");
            }
        }
        return () => { };
    }, [auth.isSignedIn(), auth.userId]);
    useEffect(() => {
        const path = window.location.search
            ? window.location.search
            : window.location.href;
        logger.log("mount: path = " + path);
        let capturedChannelOwnerId = undefined;
        let capturedChannelId = undefined;
        let capturedPostId = undefined;
        if (path.startsWith("?")) {
            const paramString = path.substring(1, path.length);
            const params = paramString.split("&");
            params.forEach((param) => {
                const fields = param.split("=");
                if (fields.length == 2) {
                    const key = fields[0];
                    const value = fields[1];
                    if ("channelOwnerId" == key) {
                        capturedChannelOwnerId = value;
                        setChannelOwnerId(capturedChannelOwnerId);
                    }
                    else if ("channelId" == key) {
                        capturedChannelId = value;
                        setChannelId(value);
                    }
                    else if ("postId" == key) {
                        capturedPostId = value;
                        if (capturedPostId.endsWith(".json")) {
                            capturedPostId = capturedPostId.substring(0, capturedPostId.length - 5);
                        }
                        setPostId(capturedPostId);
                    }
                    else {
                        logger.error("Unexpected Parameter Name: " + key);
                    }
                }
                else {
                    logger.error("Unexpected Field Count: " + fields.length);
                }
            });
        }
        if (!capturedChannelId) {
            logger.error("Missing channel ID parameter.");
            setErrorMessage("Channel ID ('channelId=') is missing from URL. Please check the URL, and try again.");
        }
        else if (!capturedPostId) {
            logger.error("Missing post ID parameter.");
            setErrorMessage("Post ID ('postId=...') is missing from URL. Please check the URL, and try again.");
        }
        else if (!capturedChannelOwnerId) {
            logger.error("Missing channel owner ID parameter.");
            setErrorMessage("Channel Owner ID ('channelOwnerId=...') is missing from URL. Please check the URL, and try again.");
        }
        return () => { };
    }, []);
    // Load post data for Open Graph meta tags
    useEffect(() => {
        logger.log("PostView: Open Graph useEffect triggered - channelOwnerId: " +
            channelOwnerId +
            ", channelId: " +
            channelId +
            ", postId: " +
            postId +
            ", hasPostData: " +
            !!postData);
        if (channelOwnerId && channelId && postId && !postData) {
            const postJsonUrl = getContentURL() +
                "/u/" +
                channelOwnerId +
                "/" +
                channelId +
                "/" +
                postId +
                ".json";
            logger.log("PostView: Loading post data for Open Graph: " + postJsonUrl);
            logger.log("Loading post data for Open Graph: " + postJsonUrl);
            loadChannelDataFromURL(postJsonUrl, {
                okCallback: (clientDetailCode, data) => {
                    try {
                        const dataObj = JSON.parse(data);
                        logger.log("PostView: Post data loaded for Open Graph: " +
                            JSON.stringify(dataObj));
                        setPostData(dataObj);
                        logger.log("Post data loaded for Open Graph");
                        // Record post click for analytics - only from /PostView page
                        if (channelOwnerId &&
                            channelId &&
                            postId &&
                            (window.location.pathname === "/PostView" ||
                                window.location.pathname.includes("/PostView"))) {
                            logger.recordPostClick(channelOwnerId, channelId, postId);
                        }
                    }
                    catch (error) {
                        logger.error("PostView: Failed to parse post data: " + error);
                        logger.error("Failed to parse post data: " + error);
                    }
                },
                errorCallback: (clientDetailCode, errorMessage) => {
                    logger.error("PostView: Failed to load post data: " + errorMessage);
                    logger.error("Failed to load post data: " + errorMessage);
                },
            }, cacheBusterStore, logger);
        }
        else {
            logger.log("PostView: Skipping post data load - hasChannelOwnerId: " +
                !!channelOwnerId +
                ", hasChannelId: " +
                !!channelId +
                ", hasPostId: " +
                !!postId +
                ", hasPostData: " +
                !!postData);
        }
    }, [channelOwnerId, channelId, postId, postData]);
    // Load channel data to check for adult content permissions
    useEffect(() => {
        if (channelOwnerId && channelId && !channelData) {
            const channelJsonUrl = getContentURL() +
                "/u/" +
                channelOwnerId +
                "/" +
                channelId +
                "/channelData.json";
            logger.log("PostView: Loading channel data for age verification: " +
                channelJsonUrl);
            loadChannelDataFromURL(channelJsonUrl, {
                okCallback: (clientDetailCode, data) => {
                    var _a;
                    try {
                        const dataObj = JSON.parse(data);
                        logger.log("PostView: Channel data loaded: " + JSON.stringify(dataObj));
                        setChannelData(dataObj);
                        // Check age verification for adult content
                        if ((_a = dataObj.permissions) === null || _a === void 0 ? void 0 : _a.permissionsAdultContent) {
                            logger.log("PostView: Channel has adult content, checking age verification...");
                            const currentUrl = window.location.pathname + window.location.search;
                            const redirectOccurred = ageVerification.checkAndRedirectIfNeeded(true, currentUrl);
                            if (redirectOccurred) {
                                logger.log("PostView: Age verification redirect initiated");
                                return; // Exit early to prevent further processing
                            }
                        }
                    }
                    catch (error) {
                        logger.error("PostView: Failed to parse channel data: " + error);
                    }
                },
                errorCallback: (clientDetailCode, errorMessage) => {
                    logger.error("PostView: Failed to load channel data: " + errorMessage);
                    // Don't set error message for channel data load failure as this is optional for age verification
                },
            }, cacheBusterStore, logger);
        }
    }, [channelOwnerId, channelId, channelData]);
    useEffect(() => {
        function handleResize() {
            logger.log("Window width = " + window.innerWidth);
            setWidth(window.innerWidth);
            logger.log("Window height = " + window.innerHeight);
            setHeight(window.innerHeight);
        }
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [setWidth]);
    const deletePostParent = (basePath, postJson) => {
        logger.log("deletePostParent: " + basePath + " " + postJson);
        if (!(auth === null || auth === void 0 ? void 0 : auth.userId)) {
            setErrorMessage("User is not logged in.");
            return;
        }
        if (!channelOwnerId) {
            setErrorMessage("Channel owner is not set.");
            return;
        }
        deletePost({
            operation: "deletePost",
            channelOwnerId: channelOwnerId,
            channelId: channelId,
            postOwnerId: auth.userId,
            postId: postJson,
        }, {
            okCallback: (clientDetailCode) => {
                logger.log("deletePost: OK " + clientDetailCode);
                // Increment cache buster after successful deletion
                cacheBusterStore.incrementCacheBuster();
                setPostDeletedOK(true);
            },
            errorCallback: (clientDetailCode, errorMessage) => {
                logger.log("deletePost: ERROR " + clientDetailCode + " " + errorMessage);
                setErrorMessage(errorMessage + " (" + clientDetailCode + ")");
            },
        }, {
            isSignedIn: () => auth.isSignedIn(),
            bearer: () => auth.bearer,
            sequence: () => auth.sequence,
            setBearerAndSequence: (bearer, sequence) => {
                auth.setBearerAndSequence(bearer, sequence, logger);
            },
            getAuth: () => {
                return auth;
            },
        }, cacheBusterStore, logger);
    };
    let postCard = undefined;
    const basePath = getContentURL() + "/u/" + channelOwnerId + "/" + channelId;
    if (channelOwnerId && channelId && postId) {
        if (postDeletedOK) {
            postCard = (_jsxs(Card, Object.assign({ elevation: 10, sx: {
                    backgroundColor: "#EEFFEE",
                    padding: layout.cardPadding,
                    margin: layout.cardMargin,
                    alignContent: "center",
                    textAlign: "center",
                } }, { children: [_jsxs(CardContent, Object.assign({ sx: {
                            padding: layout.cardContentPadding,
                            margin: layout.cardContentMargin,
                        } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: "main" }, { children: "POST DELETED" })), _jsx(Typography, Object.assign({ variant: "body1", sx: {
                                    display: "flex",
                                    justifyContent: "center",
                                    textAlign: "center",
                                } }, { children: "Your post has been deleted." }))] }), "channelview.content.cardcontent.deletedok"), _jsx(CardActions, Object.assign({ sx: {
                            padding: 0,
                            margin: 0,
                            display: "flex",
                            justifyContent: "center",
                        } }, { children: _jsx(Button, Object.assign({ variant: "contained", onClick: () => {
                                setPostDeletedOK(false);
                                setErrorMessage(undefined);
                                const newUrl = getSiteBaseURL() +
                                    "ChannelView" +
                                    "?channelOwnerId=" +
                                    channelOwnerId +
                                    "&channelId=" +
                                    channelId;
                                logger.log("Navigating to: " + newUrl);
                                window.location.href = newUrl;
                            }, sx: { margin: "0.5em" }, "aria-label": "OK" }, { children: "OK" }), ".post-deleted-ok-card.button.close") }))] }), "postview.content.card.deletedok"));
        }
        else {
            postCard = (_jsx(ContentCardStacks, { keyStr: "postcard.stack." + postId, cards: [
                    _jsx(PostCard, { basePath: basePath, postJson: postId + ".json", enableEditButtons: false, keyStr: "postcard.postview." + postId, maxResolution: true, maxMediaWidth: width, maxMediaHeight: height, channelOwnerId: channelOwnerId, channelId: channelId, postId: postId, expandPostContent: true, deletePost: deletePostParent }, "postcard.postview." + postId),
                ] }));
        }
    }
    const channelJson = "u/" + channelOwnerId + "/" + channelId + "/channelData.json";
    const channelButton = (_jsx(ChannelButton, { keyStr: "chanKeyCard." + channelId, basePath: getContentURL(), channelJson: channelJson }, "chanKeyCard." + channelId));
    const content = (_jsxs(Stack, Object.assign({ sx: { padding: 0, marging: 0 }, spacing: 0 }, { children: [errorMessage && (_jsx(Card, Object.assign({ elevation: 10, sx: {
                    backgroundColor: "#FFEEEE",
                    padding: layout.cardPadding,
                    margin: layout.cardMargin,
                    alignContent: "center",
                    textAlign: "center",
                } }, { children: _jsxs(CardContent, Object.assign({ sx: {
                        padding: layout.cardContentPadding,
                        margin: layout.cardContentMargin,
                    } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: "error" }, { children: "ERROR" })), _jsx(Typography, Object.assign({ variant: "body1", sx: {
                                textAlign: "left",
                                paddingLeft: layout.cardContentPadding,
                                paddingRight: layout.cardContentPadding,
                            } }, { children: errorMessage }))] }), "channelview.content.cardcontent.error") }), "postview.content.card.error")), channelButton, postCard] }), "postview.content.stack"));
    const output = (_jsxs("div", Object.assign({ style: { width: "100%", padding: 0, margin: 0 }, id: "outer" }, { children: [(() => {
                logger.log("PostView: Checking Open Graph conditions - hasPostData: " +
                    !!postData +
                    ", hasChannelOwnerId: " +
                    !!channelOwnerId +
                    ", hasChannelId: " +
                    !!channelId +
                    ", hasPostId: " +
                    !!postId);
                if (postData && channelOwnerId && channelId && postId) {
                    const openGraphData = generatePostOpenGraphData(postData, channelOwnerId, channelId, postId);
                    logger.log("PostView: Rendering OpenGraphMeta with data: " +
                        JSON.stringify(openGraphData));
                    return _jsx(OpenGraphMeta, { data: openGraphData });
                }
                else {
                    logger.log("PostView: Not rendering OpenGraphMeta - missing required data");
                    return null;
                }
            })(), _jsxs(ThemeProvider, Object.assign({ theme: getTheme() }, { children: [_jsx(Heading, {}), _jsx(Container, Object.assign({ sx: {
                            backgroundColor: "#FFFFFF",
                            width: "100%",
                            padding: 0,
                            margin: 0,
                        }, maxWidth: false }, { children: content })), _jsx(Footer, {})] }))] }), "postview.div"));
    return output;
};
